import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  fournisseurService,
  articleService,
  documentService,
  ocrService,
  OcrExtractResult,
} from '../../api/services';
import { Article, Fournisseur, TauxTva } from '../../types';

interface DraftLine {
  articleId: number | '';
  quantite: number;
  prixUnitaireHT: number;
  tauxTva: TauxTva;
  fodec: boolean;
}

const emptyLine = (): DraftLine => ({
  articleId: '', quantite: 1, prixUnitaireHT: 0, tauxTva: TauxTva.TVA_19, fodec: true,
});

const SupplierInvoiceForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [suppliers, setSuppliers] = useState<Fournisseur[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);

  const [fournisseurId, setFournisseurId] = useState<number | ''>('');
  const [numeroFournisseur, setNumeroFournisseur] = useState('');
  const [dateDocument, setDateDocument] = useState(() => new Date().toISOString().slice(0, 10));
  const [lignes, setLignes] = useState<DraftLine[]>([emptyLine()]);
  const [ocr, setOcr] = useState<OcrExtractResult | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([fournisseurService.getAll({ size: 1000 }), articleService.getAll({ size: 1000 })])
      .then(([fo, ar]) => {
        setSuppliers(fo.content.filter((f) => f.actif !== false));
        setArticles(ar.content.filter(
          (a) => !['HEURE', 'JOUR', 'FORFAIT', 'SERVICE'].includes(a.unite as string),
        ));
      })
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur de chargement'));
  }, []);

  // Mode édition : charger la facture d'achat existante.
  useEffect(() => {
    if (!id) return;
    documentService.getById(Number(id))
      .then((doc) => {
        if (doc.fournisseurId) setFournisseurId(doc.fournisseurId);
        if (doc.numeroFournisseur) setNumeroFournisseur(doc.numeroFournisseur);
        if (doc.dateDocument) setDateDocument(doc.dateDocument.slice(0, 10));
        const mapped: DraftLine[] = (doc.lignes ?? []).map((l) => ({
          articleId: l.articleId ?? '',
          quantite: Number(l.quantite) || 0,
          prixUnitaireHT: Number(l.prixUnitaireHT) || 0,
          tauxTva: l.tauxTva ?? TauxTva.TVA_19,
          fodec: Boolean(l.fodec),
        }));
        if (mapped.length > 0) setLignes(mapped);
      })
      .catch(() => toast.error('Facture introuvable'));
  }, [id]);

  const handleOcr = async (file: File) => {
    setExtracting(true);
    try {
      const res = await ocrService.extract(file);
      const f = res.fields;
      setOcr(f);
      const hasData = Boolean(
        f.supplier || f.number || f.date || f.totalHT || f.totalTVA || f.totalTTC,
      );
      if (!hasData) {
        // Provider "noop" (ou LLM non configuré) : on ne fait pas croire à une réussite.
        toast(
          "OCR : aucune donnée extraite. L'OCR n'est peut-être pas activé sur le serveur " +
          '(moteur de vision/clé requis) ou la facture est illisible — saisissez les champs manuellement.',
          { icon: '⚠️', duration: 6000 },
        );
        return;
      }
      if (f.number) setNumeroFournisseur(f.number);
      if (f.date) setDateDocument(f.date);
      // Rapprochement fournisseur par nom (insensible à la casse).
      if (f.supplier) {
        const match = suppliers.find(
          (s) => s.raisonSociale.toLowerCase() === f.supplier!.toLowerCase(),
        );
        if (match) setFournisseurId(match.id);
      }
      toast.success('Facture analysée — vérifiez et complétez les lignes');
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 402 || status === 403) {
        toast.error('OCR non disponible sur votre plan');
      } else {
        toast.error(e?.response?.data?.message || 'Échec de l’analyse OCR');
      }
    } finally {
      setExtracting(false);
    }
  };

  const updateLine = (i: number, patch: Partial<DraftLine>) => {
    setLignes((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  };

  const onPickArticle = (i: number, articleId: number | '') => {
    const art = articles.find((a) => a.id === articleId);
    updateLine(i, {
      articleId,
      // Pré-remplit le prix d'achat avec le CMUP courant comme point de départ.
      prixUnitaireHT: art?.cmup ?? art?.prixUnitaireHT ?? 0,
      tauxTva: art?.tauxTva ?? TauxTva.TVA_19,
      fodec: art?.fodec ?? true,
    });
  };

  const totalHT = useMemo(
    () => lignes.reduce((s, l) => s + (Number(l.quantite) || 0) * (Number(l.prixUnitaireHT) || 0), 0),
    [lignes],
  );

  const submit = async () => {
    if (fournisseurId === '') { toast.error('Sélectionnez un fournisseur'); return; }
    const valid = lignes.filter((l) => l.articleId !== '' && l.quantite > 0);
    if (valid.length === 0) { toast.error('Ajoutez au moins une ligne'); return; }

    setSaving(true);
    try {
      const payload = {
        typeDocument: 'FACTURE' as any,
        sensDocument: 'ACHAT',
        numeroFournisseur: numeroFournisseur || undefined,
        fournisseurId: Number(fournisseurId),
        dateDocument,
        lignes: valid.map((l) => ({
          articleId: Number(l.articleId),
          quantite: l.quantite,
          prixUnitaireHT: l.prixUnitaireHT,
          tauxTva: l.tauxTva,
          fodec: l.fodec,
          remise: 0,
        })),
      } as any;
      if (isEdit && id) {
        await documentService.update(Number(id), payload);
        toast.success('Facture fournisseur mise à jour — stock recalculé');
        navigate(`/documents/view/${id}`);
      } else {
        await documentService.create(payload);
        toast.success('Facture fournisseur enregistrée — stock mis à jour');
        navigate('/stock/mouvements');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Modifier la facture fournisseur' : 'Facture fournisseur'}
        </h1>
        <p className="text-sm text-gray-500">
          Réception d’achat — incrémente le stock et recalcule le CMUP. Importez une facture pour
          pré-remplir l’en-tête par OCR.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Importer une facture (OCR)
          </label>
          <input
            type="file"
            accept="application/pdf,image/*"
            disabled={extracting}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleOcr(f); }}
            className="text-sm"
          />
          {extracting && <p className="text-xs text-gray-500 mt-1">Analyse en cours…</p>}
          {ocr && (
            <p className="text-xs text-gray-500 mt-2">
              OCR — fournisseur: <strong>{ocr.supplier || '—'}</strong> · TTC:{' '}
              <strong>{ocr.totalTTC ?? '—'}</strong> · HT: <strong>{ocr.totalHT ?? '—'}</strong>
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="block text-sm">
            <span className="text-gray-700">Fournisseur</span>
            <select
              value={fournisseurId}
              onChange={(e) => setFournisseurId(e.target.value ? Number(e.target.value) : '')}
              className="mt-1 w-full rounded-lg border-gray-300 text-sm"
            >
              <option value="">— Sélectionner —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.raisonSociale}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">N° facture fournisseur</span>
            <input
              type="text" value={numeroFournisseur}
              onChange={(e) => setNumeroFournisseur(e.target.value)}
              className="mt-1 w-full rounded-lg border-gray-300 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">Date</span>
            <input
              type="date" value={dateDocument}
              onChange={(e) => setDateDocument(e.target.value)}
              className="mt-1 w-full rounded-lg border-gray-300 text-sm"
            />
          </label>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Article</th>
              <th className="px-3 py-2 text-right">Quantité</th>
              <th className="px-3 py-2 text-right">Prix d’achat HT</th>
              <th className="px-3 py-2 text-right">Sous-total</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-sm">
            {lignes.map((l, i) => (
              <tr key={i}>
                <td className="px-3 py-2">
                  <select
                    value={l.articleId}
                    onChange={(e) => onPickArticle(i, e.target.value ? Number(e.target.value) : '')}
                    className="w-full rounded-lg border-gray-300 text-sm"
                  >
                    <option value="">— Article —</option>
                    {articles.map((a) => (
                      <option key={a.id} value={a.id}>{a.reference || a.code} — {a.designation}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number" min="0" step="0.001" value={l.quantite}
                    onChange={(e) => updateLine(i, { quantite: Number(e.target.value) })}
                    className="w-24 rounded-lg border-gray-300 text-sm text-right"
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number" min="0" step="0.001" value={l.prixUnitaireHT}
                    onChange={(e) => updateLine(i, { prixUnitaireHT: Number(e.target.value) })}
                    className="w-28 rounded-lg border-gray-300 text-sm text-right"
                  />
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {((Number(l.quantite) || 0) * (Number(l.prixUnitaireHT) || 0)).toFixed(3)}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => setLignes((prev) => prev.filter((_, idx) => idx !== i))}
                    className="text-red-600 text-xs hover:underline"
                    disabled={lignes.length === 1}
                  >
                    Retirer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-3 py-3">
          <button
            onClick={() => setLignes((prev) => [...prev, emptyLine()])}
            className="text-sm text-indigo-600 hover:underline"
          >
            + Ajouter une ligne
          </button>
          <div className="text-sm font-medium text-gray-700">
            Total HT : <span className="font-mono">{totalHT.toFixed(3)}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button
          onClick={() => navigate('/stock/mouvements')}
          className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Annuler
        </button>
        <button
          onClick={submit}
          disabled={saving}
          className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer la facture'}
        </button>
      </div>
    </div>
  );
};

export default SupplierInvoiceForm;
